import { PhotoType } from "@instacover/instacar-database/browser"
import { isNotNil } from "@instacover/utils"
import { createFileRoute, useSearch } from "@tanstack/react-router"
import { z } from "zod"

import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { useSessionDetail, useStepsByType, useStepWithMedia } from "@/services/dexie"

import { BackButton } from "../../../../components/buttons/BackButton"
import { ClientRedirect } from "../../../../components/ClientRedirect"
import { useGetStepTexts } from "../../../../locales/photoType"
import { LINKS } from "../../../../routing/links"
import { useStepsRouting } from "../../../../routing/useStepsRouting"
import { DocumentMultiCaptureStep } from "../../../../sections/documents/DocumentMultiCaptureStep"
import { GuideDocumentBase } from "../../../../sections/documents/GuideDocumentBase"
import { useBacklink } from "../../../../utils/session"

const pathParamsSchema = z.object({ step: z.enum(PhotoType) })

export const Route = createFileRoute("/$sessionId/_session/documents/$step")({
  params: {
    parse: (rawParams) => pathParamsSchema.parse(rawParams),
  },
  component: DocumentStepRoute,
})

function DocumentStepRoute() {
  const { step, sessionId } = Route.useParams()
  const { key } = useSearch({ from: "/$sessionId/_session" })
  const backlink = useBacklink()

  const { data: detail } = useSessionDetail(sessionId)
  const { data: documentSteps, isLoading: docStepsLoading } = useStepsByType(sessionId, "DocumentPhoto")
  const { data: currentStep, isLoading: stepLoading } = useStepWithMedia(sessionId, step)

  const { firstUnfinishedStepLink, isLoading: stepsLoading } = useStepsRouting(sessionId)
  const getStepTexts = useGetStepTexts(detail?.vehicleType)

  if (!currentStep || !documentSteps) {
    if (stepLoading || stepsLoading || docStepsLoading) return <SpinnerLayout />
    return <ClientRedirect to={firstUnfinishedStepLink ?? LINKS.finalCheck(sessionId)} />
  }

  const { name, photos, canSkip, canUploadMultiple, enableUpload } = currentStep
  const { name: stepName, guide } = getStepTexts(name)
  const isDocumentValidityCheckEnabled = currentStep.documentValidityCheck === true

  return (
    <div>
      {backlink && <BackButton backlink={backlink} />}

      {!canUploadMultiple ? (
        <GuideDocumentBase
          key={[name, key].filter(isNotNil).join("-")}
          steps={documentSteps}
          name={stepName}
          type={name}
          guide={guide}
          photo={photos?.[0]}
          canSkip={canSkip}
          sessionId={sessionId}
          isDocumentValidityCheckEnabled={isDocumentValidityCheckEnabled}
          enableUpload={enableUpload}
        />
      ) : (
        <DocumentMultiCaptureStep
          key={[name, key].filter(isNotNil).join("-")}
          sessionId={sessionId}
          name={stepName}
          type={name}
          guide={guide}
          canSkip={canSkip}
          enableUpload={enableUpload}
        />
      )}
    </div>
  )
}
